import {
  CHANGE_SUBSCRIPTION,
  CHANGE_SUBSCRIPTION_FAILED,
  REQUEST_OWN_FEEDS,
  REQUEST_POSTS_BY_OWNER,
  REQUEST_POSTS_BY_OWNER_FAILED,
  REQUEST_SUBSCRIPTION,
  REQUEST_SUBSCRIPTION_FAILED,
  REQUEST_USER_NEWS,
  REQUEST_USER_NEWS_FAILED,
  UPDATE_POST_WS,
  UPDATE_POST_WS_FAILED,
} from '../constants/index';
import {
  ChangedSubscriptionPayloadType,
  RequestNewsPayloadType,
  RequestPostsByUserType,
  RequestSubscriptionsPayloadType,
  UpdatePostWsActionType,
  requestOwnFeedsSuccess,
  requestPostsByOwnerSuccess,
  updatePostWsSuccess,
} from '../actions/feedActions';
import { ForeignUserStoreType, RootState } from 'types/stateTypes';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { nakedSource, yandexSource } from '../../constants/linkSource';
import {
  requestSubscription,
  requestSubscriptionSuccess,
  requestUserNews,
  requestUserNewsSuccess,
} from '../actions/feedActions';
import { ActionType } from '../actions/themeActions';
import { ErrorResponseType } from 'types/reduxTypes';
import { cls } from 'constants/cls';
import config from '../../shared/config';
import { defaultPageSize } from 'constants/feed';
import safeFetch from '../../shared/api/safeFetch';

function* requestUserNewsHandler({
  payload: { id, mode, since, forOffline, pageSize = defaultPageSize },
}: ActionType<RequestNewsPayloadType>) {
  const sinceParameter = since ? `&since=${since}` : '';

  const {
    user: { isBrowserOnline },
    feed: { news, subscription: subscriptions },
  }: RootState = yield select();

  if (!isBrowserOnline && forOffline) {
    const activeSubscriptions = subscriptions.filter((item) => item.subscribed);
    const urls = [
      { name: 'naked-science', source: nakedSource, tag: 'naked' },
      { name: 'yandex.ru', source: yandexSource, tag: 'yandex' },
    ].filter((item) =>
      activeSubscriptions.some((sub) => {
        return item.source.includes(sub.source) && sub.source.includes(item.source);
      })
    );
    if (!activeSubscriptions) {
      yield put(requestUserNewsSuccess({ data: [], isBrowserOnline: false, mode, pageSize }));
      return;
    }
    const hasNaukotheka = activeSubscriptions.some((item) => item.name === 'naukoteka');
    const filteredNews = news.filter((item) => {
      if (!item?.body?.link) {
        return hasNaukotheka;
      }
      return urls.some((url) => item?.body?.link?.includes(url.name));
    });
    yield put(requestUserNewsSuccess({ data: filteredNews, isBrowserOnline: false, mode, pageSize }));
    return;
  }

  try {
    const { data } = yield call(
      safeFetch,
      `${config.apiUrl}/user_messages/list/${id}?pageSize=${pageSize}${sinceParameter}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      }
    );

    yield put(requestUserNewsSuccess({ data, mode, pageSize }));
  } catch (error: unknown) {
    const err = error as ErrorResponseType;
    yield put({
      payload: { message: (err.response && err.response.status) || err.message },
      type: REQUEST_USER_NEWS_FAILED,
    });
  }
}

function* requestSubscriptionHandler({ payload: { id } }: ActionType<RequestSubscriptionsPayloadType>) {
  try {
    const { data } = yield call(safeFetch, `${config.apiUrl}/user_messages/channels/subscriptions/${id}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    yield put(requestSubscriptionSuccess({ subscription: data }));
  } catch (error: unknown) {
    const err = error as ErrorResponseType;
    yield put({
      payload: { message: (err.response && err.response.status) || err.message },
      type: REQUEST_SUBSCRIPTION_FAILED,
    });
  }
}

function* changeSubscriptionHandler({
  payload: { userId, query, forOffline },
}: ActionType<ChangedSubscriptionPayloadType>) {
  const {
    user: { isBrowserOnline },
    feed: { subscription: subscriptions },
  }: RootState = yield select();

  let fetchSubscriptions = subscriptions;
  if (!forOffline) {
    const subscrIndex = subscriptions.findIndex((item) => item.source === query.source);
    subscriptions[subscrIndex] = { ...subscriptions[subscrIndex], subscribed: query.subscribed };
    const newSubscriptions = subscriptions.slice();

    if (!isBrowserOnline) {
      yield put(requestSubscriptionSuccess({ forOffline: true, subscription: newSubscriptions }));
      yield put(requestUserNews({ forOffline: true, mode: 'replace' }));
      return;
    }

    fetchSubscriptions = newSubscriptions.map((item) => {
      delete item.name;
      return item;
    });
  }

  try {
    yield call(safeFetch, `${config.apiUrl}/user_messages/channels/subscriptions/${userId}`, {
      body: fetchSubscriptions,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    yield put(requestSubscription({ id: userId }));
  } catch (error: unknown) {
    const err = error as ErrorResponseType;
    const message = err.response || err.message;
    yield put({
      payload: { message },
      type: CHANGE_SUBSCRIPTION_FAILED,
    });
  }
}

function* requestPostsByOwnerHandler({
  payload: { userId, since, pageSize = defaultPageSize },
}: ActionType<RequestPostsByUserType>) {
  const sinceParameter = since ? `&timeTo=${since}` : '';
  try {
    const { data } = yield call(
      safeFetch,
      `${config.apiUrl}/feed/items/by_owner/${cls.user}:${userId}?pageSize=${pageSize}${sinceParameter}`,
      {
        body: {},
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      }
    );
    yield put(
      requestPostsByOwnerSuccess({ pageSize, publications: data as unknown as ForeignUserStoreType['publications'] })
    );
  } catch (error: unknown) {
    const err = error as ErrorResponseType;
    yield put({
      payload: { message: (err.response && err.response.status) || err.message },
      type: REQUEST_POSTS_BY_OWNER_FAILED,
    });
  }
}

function* requestOwnFeedsHandler({ payload }: ActionType<RequestSubscriptionsPayloadType>) {
  try {
    const { data } = yield call(safeFetch, `${config.apiUrl}/feed/writable`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(requestOwnFeedsSuccess(data));
  } catch (error: unknown) {
    const err = error as ErrorResponseType;
    yield put({
      payload: { message: (err.response && err.response.status) || err.message },
      type: REQUEST_SUBSCRIPTION_FAILED,
    });
  }
}

function* updatePostWsHandler({ payload }: ActionType<UpdatePostWsActionType>) {
  const { postId, actionType } = payload;
  try {
    const { data } =
      actionType === 'update'
        ? yield call(safeFetch, `${config.apiUrl}/post/${postId}`, {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            method: 'GET',
          })
        : { data: null };
    yield put(updatePostWsSuccess({ actionType, post: data, postId }));
  } catch (error: unknown) {
    const err = error as ErrorResponseType;
    yield put({
      payload: { message: (err.response && err.response.status) || err.message },
      type: UPDATE_POST_WS_FAILED,
    });
  }
}

export default function* fetchUserWatcher(): Generator {
  yield takeEvery(REQUEST_USER_NEWS, requestUserNewsHandler);
  yield takeEvery(REQUEST_SUBSCRIPTION, requestSubscriptionHandler);
  yield takeEvery(CHANGE_SUBSCRIPTION, changeSubscriptionHandler);
  yield takeEvery(REQUEST_POSTS_BY_OWNER, requestPostsByOwnerHandler);
  yield takeEvery(REQUEST_OWN_FEEDS, requestOwnFeedsHandler);
  yield takeEvery(UPDATE_POST_WS, updatePostWsHandler);
}
